import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Server, Cpu, HardDrive, Activity, AlertTriangle, CheckCircle, XCircle,
  RefreshCw, Clock, Wifi, WifiOff, Timer, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MetricGauge({ value, label, icon: Icon, max = 100 }) {
  const pct = Math.min(Math.max(value, 0), max);
  const color = pct >= 90 ? "text-red-400" : pct >= 70 ? "text-yellow-400" : "text-green-400";
  const ringColor = pct >= 90 ? "stroke-red-500" : pct >= 70 ? "stroke-yellow-500" : "stroke-green-500";
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (pct / max) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
          <circle cx="40" cy="40" r="36" fill="none" className={ringColor} strokeWidth="6"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold font-mono ${color}`}>{Math.round(pct)}%</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-zinc-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
    </div>
  );
}

function ServerMetricCard({ server, metrics, collecting }) {
  const isOnline = server.status === "online";
  const data = metrics || server;

  return (
    <Card className={`bg-[#0c0c0e] border-white/5 ${isOnline ? "hover:border-green-500/20" : "hover:border-red-500/20"} transition-all`}
      data-testid={`server-metric-${server.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? "bg-green-400 animate-pulse" : "bg-red-400"}`} />
            <h3 className="text-white text-sm font-medium truncate">{server.name}</h3>
            <Badge className="text-[10px] bg-zinc-800 text-zinc-400 border-zinc-700 shrink-0">{server.os_type}</Badge>
          </div>
          <div className="flex items-center gap-1.5">
            {server.ssh_configured ? (
              <Wifi className="w-3 h-3 text-green-400" />
            ) : (
              <WifiOff className="w-3 h-3 text-zinc-600" />
            )}
            {collecting && <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />}
          </div>
        </div>
        <div className="flex items-center justify-around">
          <MetricGauge value={data.cpu_usage || 0} label="CPU" icon={Cpu} />
          <MetricGauge value={data.memory_usage || 0} label="Memory" icon={Activity} />
          <MetricGauge value={data.disk_usage || 0} label="Disk" icon={HardDrive} />
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-600">
          <span className="font-mono">{server.ip_address}</span>
          <span>{data.uptime && data.uptime !== "N/A" ? `Up: ${data.uptime}` : ""}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverMetrics, setServerMetrics] = useState({});
  const [collecting, setCollecting] = useState(false);
  const [intervalMin, setIntervalMin] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard-stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInterval = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/metrics-interval`);
      setIntervalMin(res.data.interval_minutes || 30);
    } catch { /* use default */ }
  }, []);

  const collectAllMetrics = useCallback(async () => {
    if (!stats?.servers?.length) return;
    setCollecting(true);
    const sshServers = stats.servers.filter(s => s.ssh_configured);
    const results = {};
    for (const server of sshServers) {
      try {
        const res = await axios.get(`${API}/metrics/${server.id}`);
        if (res.data && !res.data.error) {
          results[server.id] = res.data;
        }
      } catch { /* skip */ }
    }
    setServerMetrics(results);
    setLastRefresh(new Date());
    setCollecting(false);
    // Re-fetch stats to get updated server data
    await fetchStats();
  }, [stats, fetchStats]);

  useEffect(() => {
    fetchStats();
    fetchInterval();
  }, [fetchStats, fetchInterval]);

  // Auto-refresh based on admin-configured interval
  useEffect(() => {
    if (intervalMin < 1) return;
    const ms = intervalMin * 60 * 1000;
    const timer = setInterval(() => {
      fetchStats();
      collectAllMetrics();
    }, ms);
    return () => clearInterval(timer);
  }, [intervalMin, fetchStats, collectAllMetrics]);

  const getStatusColor = (status) => {
    switch (status) {
      case "online": return "text-green-400";
      case "warning": return "text-yellow-400";
      case "offline": return "text-red-400";
      default: return "text-zinc-400";
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Dashboard</h1>
          <p className="text-zinc-400 text-sm mt-1">Server health overview and live monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Timer className="w-3 h-3" />
            <span>Refresh: {intervalMin}m</span>
            {lastRefresh && <span>&middot; Last: {lastRefresh.toLocaleTimeString()}</span>}
          </div>
          <Button onClick={collectAllMetrics} disabled={collecting} variant="outline"
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white" data-testid="collect-metrics-btn">
            {collecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {collecting ? "Collecting..." : "Collect Metrics"}
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0c0c0e] border-white/5 hover:border-blue-500/30 transition-colors">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-xs">Total Servers</p>
                <p className="text-3xl font-bold font-mono text-white mt-1">{stats?.total_servers || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5 hover:border-green-500/30 transition-colors">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-xs">Online</p>
                <p className="text-3xl font-bold font-mono text-green-400 mt-1">{stats?.online_servers || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5 hover:border-yellow-500/30 transition-colors">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-xs">Warning</p>
                <p className="text-3xl font-bold font-mono text-yellow-400 mt-1">{stats?.warning_servers || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5 hover:border-red-500/30 transition-colors">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-xs">Offline</p>
                <p className="text-3xl font-bold font-mono text-red-400 mt-1">{stats?.offline_servers || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Averages Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[
          { label: "Avg CPU", value: stats?.avg_cpu || 0, icon: Cpu },
          { label: "Avg Memory", value: stats?.avg_memory || 0, icon: Activity },
          { label: "Avg Disk", value: stats?.avg_disk || 0, icon: HardDrive },
        ].map(m => (
          <Card key={m.label} className="bg-[#0c0c0e] border-white/5">
            <CardContent className="p-4 flex items-center gap-4">
              <MetricGauge value={m.value} label={m.label} icon={m.icon} />
              <div>
                <p className="text-2xl font-bold font-mono text-white">{m.value}%</p>
                <p className="text-xs text-zinc-500">{m.label} across all servers</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-Server Live Metrics */}
      {stats?.servers?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium text-white">Server Metrics</h2>
            <div className="flex gap-3 text-xs text-zinc-500">
              <span>{stats.linux_servers} Linux</span>
              <span>{stats.windows_servers} Windows</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stats.servers.map(server => (
              <ServerMetricCard key={server.id} server={server} metrics={serverMetrics[server.id]} collecting={collecting} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!stats?.servers || stats.servers.length === 0) && (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-12 text-center">
            <Server className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No servers added yet</h3>
            <p className="text-zinc-400 mb-4">Add your first server to start monitoring</p>
            <a href="/servers">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-first-server-btn">Add Server</Button>
            </a>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <a href="/terminal" className="block">
          <Card className="bg-[#0c0c0e] border-white/5 hover:border-blue-500/20 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Server className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Direct Terminal</p>
                <p className="text-xs text-zinc-500">Execute commands via SSH</p>
              </div>
            </CardContent>
          </Card>
        </a>
        <a href="/runbooks" className="block">
          <Card className="bg-[#0c0c0e] border-white/5 hover:border-purple-500/20 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Activity className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Runbooks</p>
                <p className="text-xs text-zinc-500">Run multi-step command templates</p>
              </div>
            </CardContent>
          </Card>
        </a>
        <a href="/assistant" className="block">
          <Card className="bg-[#0c0c0e] border-white/5 hover:border-green-500/20 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">AI Assistant</p>
                <p className="text-xs text-zinc-500">Ask AI for help</p>
              </div>
            </CardContent>
          </Card>
        </a>
      </div>
    </div>
  );
}
